import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import moment from "moment";
import { describeCompanies, describePaCompany, splitFromPolicy, describeParts } from "./companyRules";
import { payableRowsOf } from "./insurerPayable";
import { shownPaymentLink } from "./paymentLinkView";
import { currentBrand } from "./brand";
import { financierView } from "./financierView";

/**
 * Build a PDF of an Online Policy's details.
 *
 * Drawn from the policy DATA rather than screenshotted from the page: a
 * rasterised screenshot is unsearchable, blurry when printed, and carries
 * whatever happened to be scrolled into view.
 *
 * Laid out as a document rather than a data dump. The first version was a
 * full-grid table of every field at equal weight, which read like a database
 * export: the premium was buried among identical cells, long values wrapped
 * inside a quarter-width column while three cells sat empty beside them, and
 * raw statuses like "failed_post_submission" were printed as-is.
 */

const NAVY = [30, 58, 95];
const INK = [16, 24, 40];
const GREY = [102, 112, 133];
const LINE = [234, 236, 240];
const STRIPE = [250, 251, 252];
const BLUE = [25, 118, 210];

const M = 14; // page margin, mm

const money = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? `Rs. ${n.toLocaleString("en-IN")}` : "-";
};

const text = (v) =>
  v === null || v === undefined || v === "" ? "-" : String(v);

const date = (v) => {
  if (!v) return "-";
  const m = moment(v);
  return m.isValid() ? m.format("DD MMM YYYY") : "-";
};

/**
 * Rows for a section. Empty values are dropped, so a sparse policy does not
 * print a page of dashes.
 *
 * A pair marked `wide` takes the whole line — an address squeezed into a
 * quarter of the page wrapped over five lines while three cells sat empty.
 */
const rows = (pairs) =>
  pairs
    .filter(([, value]) => value !== undefined && value !== null && value !== "")
    .map(([label, value, wide]) => ({ label, value: text(value), wide }));

/** A section: a navy title bar, then its rows. Returns the next y. */
const section = (doc, title, items, startY) => {
  if (!items.length) return startY;

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const usable = pageWidth - M * 2;

  // Never leave a title stranded at the foot of a page.
  let y = startY;
  if (y > pageHeight - 40) {
    doc.addPage();
    y = 22;
  }

  doc.setFillColor(...NAVY);
  doc.roundedRect(M, y, usable, 7.5, 1.5, 1.5, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(9.5);
  doc.setFont(undefined, "bold");
  doc.text(title.toUpperCase(), M + 3, y + 5.2);

  // Two pairs per line; a wide one takes a line of its own.
  const body = [];
  let pending = null;
  for (const item of items) {
    if (item.wide) {
      if (pending) {
        body.push([pending.label, pending.value, "", ""]);
        pending = null;
      }
      body.push([item.label, { content: item.value, colSpan: 3 }]);
      continue;
    }
    if (pending) {
      body.push([pending.label, pending.value, item.label, item.value]);
      pending = null;
    } else {
      pending = item;
    }
  }
  if (pending) body.push([pending.label, pending.value, "", ""]);

  autoTable(doc, {
    startY: y + 9,
    body,
    // "plain" with stripes rather than "grid": a cage of lines around every
    // value is what made this read as a spreadsheet.
    theme: "plain",
    styles: {
      fontSize: 8.5,
      cellPadding: { top: 2.2, bottom: 2.2, left: 3, right: 3 },
      textColor: INK,
      overflow: "linebreak",
      valign: "middle",
    },
    alternateRowStyles: { fillColor: STRIPE },
    columnStyles: {
      0: { cellWidth: 34, textColor: GREY, fontStyle: "bold" },
      1: { cellWidth: usable / 2 - 34 },
      2: { cellWidth: 34, textColor: GREY, fontStyle: "bold" },
      3: { cellWidth: "auto" },
    },
    margin: { left: M, right: M },
  });

  return doc.lastAutoTable.finalY + 7;
};

/**
 * Fetch an image as a data URL jsPDF can embed. Null on any failure — a
 * document that will not load must not stop the PDF being produced.
 */
const toDataUrl = async (url) => {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    if (!/^image\//.test(blob.type)) return null; // addImage cannot take a PDF
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    return null;
  }
};

/** A title bar on its own, for the blocks that are not field tables. */
const bar = (doc, title, y) => {
  const usable = doc.internal.pageSize.getWidth() - M * 2;
  doc.setFillColor(...NAVY);
  doc.roundedRect(M, y, usable, 7.5, 1.5, 1.5, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(9.5);
  doc.setFont(undefined, "bold");
  doc.text(title.toUpperCase(), M + 3, y + 5.2);
  return y + 13;
};

/**
 * The brand logo as something jsPDF can draw: { dataUrl, width, height } in pixels, or null. A logo that cannot be
 * loaded must never stop the report from being made, so every failure is just "no logo".
 */
export const loadLogo = async (url) => {
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    const blob = await response.blob();
    if (!/^image\/(png|jpeg)$/i.test(blob.type || "")) return null;
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
    const size = await new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = () => resolve(null);
      img.src = dataUrl;
    });
    if (!size || !size.width || !size.height) return null;
    return { dataUrl, ...size, format: /jpeg/i.test(blob.type) ? "JPEG" : "PNG" };
  } catch (e) {
    return null;
  }
};

/**
 * @param {object} p - the policy document as the view page receives it
 * @param {object} [opts]
 * @param {object} [opts.docUrls] - presigned URLs by name { panCard, aadharCard, policyPdf, proposalPdf,
 *   briskCertificate }. The PDFs are listed under Links as "Download".
 * @param {string} [opts.pageUrl] - this policy's page in the portal: the link that outlives the presigned ones
 * @returns {Promise<string>} the filename written
 */
export const downloadPolicyPdf = async (p, opts = {}) => {
  const { docUrls = {}, pageUrl = "" } = opts;
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const usable = pageWidth - M * 2;

  // The brand follows the domain the app is opened on (Rayal / Agaram), like the tab title and the login page.
  const brand = currentBrand();
  // The company's own lockup (mark + name drawn together), so logo and name are one thing rather than a picture next to
  // typed text. Without it the name is typed in the brand colour.
  const logo = await loadLogo(brand.wordmarkUrl);

  // ── Masthead ──────────────────────────────────────────────────────────────
  // A white header: the company's colours read as themselves (a blue / gold mark on navy did not), and the navy band
  // and the brand-colour rule frame it.
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, pageWidth, 4, "F");
  doc.setFillColor(...brand.accent);
  doc.rect(0, 4, pageWidth, 1.2, "F");

  const LOGO_MAX_W = 72;
  const LOGO_MAX_H = 17;
  const logoY = 8.5;
  let nameDrawn = false;
  if (logo) {
    const scale = Math.min(LOGO_MAX_W / logo.width, LOGO_MAX_H / logo.height);
    try {
      doc.addImage(logo.dataUrl, logo.format, M, logoY, logo.width * scale, logo.height * scale);
      nameDrawn = true;
    } catch (e) {
      /* fall back to the typed name below */
    }
  }
  if (!nameDrawn) {
    doc.setTextColor(...NAVY);
    doc.setFontSize(18);
    doc.setFont(undefined, "bold");
    doc.text(brand.name.toUpperCase(), M, logoY + 10);
  }

  doc.setFontSize(7.5);
  doc.setFont(undefined, "bold");
  doc.setTextColor(...GREY);
  doc.text("ONLINE POLICY DETAILS", M, 29, { charSpace: 0.6 });
  doc.setDrawColor(...LINE);
  doc.line(M, 31.5, pageWidth - M, 31.5);

  doc.setFont(undefined, "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(...GREY);
  doc.text(
    `Generated ${moment().format("DD MMM YYYY, hh:mm A")}`,
    pageWidth - M,
    22,
    { align: "right" }
  );

  // ── Summary strip: the three facts the file is opened for ─────────────────
  //
  // The reference tile follows the SAME order of authority the policy list
  // uses, so the number on the report is the number on screen:
  //   policyNumber   — the issued policy;
  //   proposalNumber — the insurer's referral number, which exists in the gap
  //                    between submitting and issuing;
  //   policyId       — the OP… id generated on save.
  // The LABEL moves with it. Printing a proposal number under the words
  // "POLICY NUMBER" would be a plain misstatement on a document people forward
  // to customers.
  const reference = p.policyNumber
    ? ["POLICY NUMBER", p.policyNumber]
    : p.proposalNumber
      ? ["PROPOSAL NUMBER", p.proposalNumber]
      : ["REFERENCE NO", p.policyId || "-"];

  const summary = [
    [reference[0], reference[1], false],
    ["VEHICLE", [p.vehicleMake, p.vehicleModel].filter(Boolean).join(" ") || "-", false],
    ["FINAL PREMIUM", money(p.finalPremium), true],
  ];
  const boxW = (usable - 8) / 3;
  const boxY = 36;
  let x = M;
  summary.forEach(([k, v, highlight]) => {
    doc.setFillColor(...(highlight ? [240, 247, 255] : [248, 249, 250]));
    doc.setDrawColor(...(highlight ? [178, 221, 255] : LINE));
    doc.roundedRect(x, boxY, boxW, 16, 2, 2, "FD");

    doc.setFontSize(6.5);
    doc.setFont(undefined, "bold");
    doc.setTextColor(...GREY);
    doc.text(k, x + 3, boxY + 5.5);

    doc.setFontSize(highlight ? 12 : 10);
    doc.setTextColor(...(highlight ? NAVY : INK));
    // Trimmed so a long policy number cannot spill out of its box.
    let shown = String(v);
    while (doc.getTextWidth(shown) > boxW - 6 && shown.length > 4) {
      shown = shown.slice(0, -2);
    }
    doc.text(shown, x + 3, boxY + 12.5);
    x += boxW + 4;
  });

  let y = boxY + 24;

  y = section(
    doc,
    "Vehicle & Policy",
    rows([
      // All three references spelled out. The tile above can only carry one,
      // and a support call may be chasing any of them. rows() drops whichever
      // do not exist yet.
      ["Policy No", p.policyNumber],
      ["Proposal No", p.proposalNumber],
      ["Reference No", p.policyId],
      // A policy sold from a Combined Make & Model has a vehicle on each side.
      ["Combined Make & Model", p.makeModelComboName],
      [p.tpVehicle ? "OD Make & Model" : "Make & Model", [p.vehicleMake, p.vehicleModel].filter(Boolean).join(" - ")],
      [
        "TP Make & Model",
        p.tpVehicle
          ? [
              [p.tpVehicle.vehicleMake, p.tpVehicle.vehicleModel].filter(Boolean).join(" - "),
              p.tpVehicle.vehicleVariant,
              p.tpVehicle.vehicleCC ? `${p.tpVehicle.vehicleCC} CC` : "",
            ]
              .filter(Boolean)
              .join(" · ")
          : "",
      ],
      ["Company", describeCompanies(p) || p.company],
      ["Policy Type", p.policyType],
      ["CC", p.vehicleCC],
      ["Variant", p.vehicleVariant],
      ["IDV", p.idv ? money(p.idv) : ""],
      ["RTO", p.rtoCityLocation],
      ["Manufacturing", [p.manufacturingMonth, p.manufacturingYear].filter(Boolean).join("/")],
      ["Engine No", p.engineNumber],
      ["Chassis No", p.chassisNumber],
      ["Purchased", p.purchaseDate ? date(p.purchaseDate) : ""],
      ["Registered", p.registrationDate ? date(p.registrationDate) : ""],
      ["Created", p.createdAt ? date(p.createdAt) : ""],
      ["Updated", p.updatedAt ? date(p.updatedAt) : ""],
    ]),
    y
  );

  y = section(
    doc,
    "Premium",
    rows([
      ["Net Premium", p.netPremium ? money(p.netPremium) : ""],
      ["Final Premium", p.finalPremium ? money(p.finalPremium) : ""],
      // Multi-company: the premium as divided between the insurers selling it.
      ...(splitFromPolicy(p)?.isSplit
        ? splitFromPolicy(p).byCompany.map((c) => [
            `${c.company} (${describeParts(c.parts)})`,
            money(c.total),
          ])
        : []),
      // What the insurer asked for when the policy was paid (the Reliance CDT amount), beside what we calculated.
      ...payableRowsOf(p).flatMap((r) => [
        [`${r.label} Calculated Amount`, r.calculatedText],
        [`${r.label} ${r.amountLabel.replace(/ amount$/, " Amount")}`, r.cdtText],
        [`${r.label} Difference`, r.differenceText],
      ]),
      ["Zero Dep", p.zeroDepreciation ? "Yes" : "No"],
      ["TPPD Restrict", p.tppdRestrict ? "Yes" : "No"],
      [
        "PA Cover",
        p.paCover
          ? `Yes${p.paCoverAmount ? ` (${money(p.paCoverAmount)})` : ""}${
              describePaCompany(p) ? ` — ${describePaCompany(p)}` : ""
            }`
          : "No",
      ],
      ["Paid By", p.Paymentmethod],
    ]),
    y
  );

  const address = [
    p.flatDoorNo,
    p.floorNo,
    p.buildingName,
    p.blockName,
    p.roadStreetLane,
    p.areaAndLocality,
    p.pincode,
    p.country,
  ]
    .filter(Boolean)
    .join(", ");

  y = section(
    doc,
    "Customer",
    rows([
      ["Name", [p.firstName, p.lastName].filter(Boolean).join(" ") || p.fullName],
      ["Date of Birth", p.dateOfBirth ? date(p.dateOfBirth) : ""],
      ["Gender", p.gender],
      ["Title", p.proposerTitle],
      ["Father's Name", p.fatherName],
      ["Mobile", p.mobileNumber],
      ["Email", p.email],
      ["Address", address, true],
    ]),
    y
  );

  y = section(
    doc,
    "Nominee & Financier",
    rows([
      ["Nominee", p.nomineeName],
      ["Relationship", p.nomineeRelation || p.nomineeRelationship],
      ["Nominee DOB", p.nomineeDob ? date(p.nomineeDob) : ""],
      ["Financier Type", financierView(p).showType ? p.financierType : ""],
      ["Financier", p.financierName],
      [financierView(p).addressLabel, p.financierAddress, true],
    ]),
    y
  );

  // Details, links and files only (the owner, 2026-10-30): no status, no automation steps, no errors. Those are on the
  // View page; this file gets forwarded.

  // ── Links ─────────────────────────────────────────────────────────────────
  // Clickable, and labelled: a wall of raw S3 URLs was unreadable and nobody
  // retypes one anyway.
  // [label, url, verb]: the policy's own files come first and say "Download"; the rest are pages and say "Open".
  const links = [
    ["Policy PDF", docUrls.policyPdf, "Download"],
    ["Proposal PDF", docUrls.proposalPdf, "Download"],
    ["Brisk Certificate", docUrls.briskCertificate, "Download"],
    ["KYC Link", p.kycLink],
    ["KYC - OVD", p.kycUrls?.ovdUrl],
    ["KYC - Upload", p.kycUrls?.uploadUrl],
    ["Proposal", p.proposalLink],
    ["Payment", shownPaymentLink(p)],
    ["PAN Card", docUrls.panCard],
    ["Aadhaar Card", docUrls.aadharCard],
    ["Policy Page", pageUrl, "Open (sign in)"],
  ].filter(([, url]) => !!url);
  const hasFileLinks = !!(docUrls.policyPdf || docUrls.proposalPdf || docUrls.briskCertificate);

  if (links.length) {
    if (y > pageHeight - 45) {
      doc.addPage();
      y = 22;
    }
    y = bar(doc, "Links", y);

    doc.setFontSize(8.5);
    links.forEach(([name, url, verb], i) => {
      if (y > pageHeight - 18) {
        doc.addPage();
        y = 22;
      }
      if (i % 2 === 1) {
        doc.setFillColor(...STRIPE);
        doc.rect(M, y - 4, usable, 6.5, "F");
      }
      doc.setFont(undefined, "bold");
      doc.setTextColor(...GREY);
      doc.text(name, M + 3, y);

      doc.setFont(undefined, "normal");
      doc.setTextColor(...BLUE);
      doc.textWithLink(verb || "Open", M + 42, y, { url });
      y += 6.5;
    });
    if (hasFileLinks) {
      // The file links are presigned: they stop working a few minutes after this PDF is made. Say so, and point at the
      // one link that does not expire.
      doc.setFont(undefined, "italic");
      doc.setFontSize(7.5);
      doc.setTextColor(...GREY);
      doc.text(
        "Download links work for a few minutes after this file is made. Later, open the Policy Page link (sign in required).",
        M + 3,
        y
      );
      y += 6;
    }
    y += 4;
  }

  // ── Documents, one per page so they can actually be read ──────────────────
  const images = [
    ["PAN Card", docUrls.panCard],
    ["Aadhaar Card", docUrls.aadharCard],
  ].filter(([, url]) => !!url);

  for (const [name, url] of images) {
    const dataUrl = await toDataUrl(url);
    if (!dataUrl) continue;

    doc.addPage();
    bar(doc, name, 16);

    try {
      const props = doc.getImageProperties(dataUrl);
      const maxW = usable;
      const maxH = pageHeight - 48;
      // Fit inside the page keeping the aspect ratio, so a tall photo is not
      // stretched across the width.
      const scale = Math.min(maxW / props.width, maxH / props.height);
      const w = props.width * scale;
      const h = props.height * scale;
      doc.addImage(dataUrl, M + (maxW - w) / 2, 32, w, h);
    } catch (e) {
      doc.setFontSize(8.5);
      doc.setTextColor(...GREY);
      doc.setFont(undefined, "normal");
      doc.text("This document could not be embedded - use the link above.", M, 34);
    }
  }

  // ── Footer on every page ──────────────────────────────────────────────────
  const pages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setDrawColor(...LINE);
    doc.line(M, pageHeight - 12, pageWidth - M, pageHeight - 12);
    doc.setFontSize(7.5);
    doc.setFont(undefined, "normal");
    doc.setTextColor(...GREY);
    doc.text(brand.name, M, pageHeight - 7);
    doc.text(`Page ${i} of ${pages}`, pageWidth - M, pageHeight - 7, {
      align: "right",
    });
  }

  const safe = (v) => String(v || "").replace(/[^a-zA-Z0-9_-]/g, "_");
  // Named after the same reference the report leads with, so the file on disk
  // and the number on the page agree.
  const filename = `${brand.filePrefix}_Policy_${safe(
    p.policyNumber || p.proposalNumber || p.policyId || p._id
  )}.pdf`;
  doc.save(filename);
  return filename;
};
